import type { GridColumnGroupingModel } from '@repo/go-table';
import { GRID_ROW_NUMBER_COL_DEF } from '@repo/go-table';
import { action, computed, flow, makeObservable, observable } from 'mobx';

import type { TTableSettingsModel } from 'src/services/user-settings-service/types';
import type { IUserSettingsService } from 'src/services/user-settings-service/user-settings-service';
import { equalsDeep } from 'src/shared/utils/equals-deep';
import type { NotificationsStore } from 'src/store/notifications-store/notifications-store';

export const SERVICE_COLUMNS = [];
export const MIN_VISIBLE_COLUMNS = 3;
export const READ_MODE_HIDDEN_COLUMNS = [];

export class DisplayManager {
  @observable private _settings: TTableSettingsModel;
  // Приходится хранить тут, т.к. MUI не позволяет достать columnGrouping из apiRef
  @observable.ref columnsGrouping: GridColumnGroupingModel = [];

  constructor(
    initialSettings: TTableSettingsModel,
    private readonly userSettingsService: IUserSettingsService,
    private readonly notifications: NotificationsStore,
  ) {
    this._settings = initialSettings;

    makeObservable(this);
  }

  @flow.bound
  private async *updateSettings(newSettings: TTableSettingsModel) {
    if (equalsDeep(newSettings, this._settings)) {
      return;
    }

    this._settings = newSettings;

    try {
      await this.userSettingsService.updateSettings('wells', newSettings);
      yield;
    } catch (e) {
      console.error(e);
      yield;
      this.notifications.showErrorMessageT('wells:Errors.failedToUpdateTableSettings');
    }
  }

  @computed
  get settings(): TTableSettingsModel {
    return this._settings;
  }

  @computed
  get displaySettingPinnedColumns(): string[] {
    const excludeColumns = [GRID_ROW_NUMBER_COL_DEF.field];

    const leftPinnedColumns = this._settings.pinnedColumns?.left || [];
    const rightPinnedColumns = this._settings.pinnedColumns?.right || [];

    const includedLeftColumns = leftPinnedColumns.filter((column) => !excludeColumns.includes(column));

    return [...includedLeftColumns, ...rightPinnedColumns];
  }

  @computed
  get displaySettingColumns(): string[] {
    const displayedColumns = this._settings?.columnsOrderModel?.filter(
      (column) => !this.displaySettingPinnedColumns.includes(column),
    );
    return displayedColumns ?? [];
  }

  @computed
  get columnsVisibilityModel() {
    return this._settings.columnsVisibility ?? {};
  }

  @computed
  get sortingModel() {
    return this._settings.sortingModel;
  }

  @computed
  get columnsOrderModel() {
    return this._settings.columnsOrderModel;
  }

  @computed
  get pinnedColumns() {
    return this._settings.pinnedColumns;
  }

  @action.bound
  onFilterModelChange(model: TTableSettingsModel['filterModel']): void {
    const newSettings = { ...this._settings, filterModel: model };

    this.updateSettings(newSettings);
  }

  @action.bound
  onColumnsOrderModelChange(columns: string[]): void {
    const newSettings = { ...this._settings, columnsOrderModel: columns };

    this.updateSettings(newSettings);
  }

  @action.bound
  onSortChange(model: TTableSettingsModel['sortingModel']): void {
    const newSettings = { ...this._settings, sortingModel: model };

    this.updateSettings(newSettings);
  }

  @action.bound
  onPinChange(pinnedColumns: TTableSettingsModel['pinnedColumns']): void {
    const newSettings = { ...this._settings, pinnedColumns };

    this.updateSettings(newSettings);
  }

  @action.bound
  onWidthChange(incomeColumnsWidth: Record<string, number>): void {
    const newWidthSettings: Record<string, number> = { ...this._settings.columnsWidth, ...incomeColumnsWidth };
    const newSettings = { ...this._settings, columnsWidth: newWidthSettings };

    this.updateSettings(newSettings);
  }

  @action.bound
  onColumnVisibilityChange(model: Record<string, boolean>): void {
    const newModel: Record<string, boolean> = { ...this._settings.columnsVisibility, ...model };

    const newSettings = { ...this._settings, columnsVisibility: newModel };

    this.updateSettings(newSettings);
  }

  @action.bound
  setColumnsGrouping(grouping: GridColumnGroupingModel): void {
    this.columnsGrouping = grouping;
  }
}
